import {useMemo} from 'react'
import dayjs from 'dayjs'
import {Link, generatePath} from 'react-router-dom'
import {Virtuoso} from 'react-virtuoso'
import {createColumnHelper, flexRender, getCoreRowModel, useReactTable} from '@tanstack/react-table'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Badge} from '@/components/ui/badge/Badge'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {CheckCircleIcon, Send01Icon} from '@/components/ui/icon/Icon'
import {
    TableBodyCell,
    TableBodyCellText,
    TableBodyRow,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellLabel
} from '@/components/ui/table-atoms/TableAtoms'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {SiarExportProject} from '@/features/projects/siar-bulk-export/types'
import {
    siarExportTransmissionStatusToBadgeVariant,
    siarExportTransmissionStatusToLabel
} from '@/features/projects/siar-bulk-export/utils'
import {
    StyledBulkSiarExportPreviewTableInner,
    StyledBulkSiarExportPreviewTableWrapper,
    StyledBulkSiarExportPreviewVirtuosoBody
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/style'

type SiarExportDetailTableProps = {
    items: SiarExportProject[]
    isSelected: (projectId: string) => boolean
    onToggle: (projectId: string) => void
    onToggleAll: () => void
    masterChecked: boolean
    masterIndeterminate: boolean
}

const formatDate = (value: string | null): string => (value ? dayjs(value).format('DD/MM/YYYY') : '—')
const formatPriCode = (code: number): string => `PRI-${String(code).padStart(4, '0')}`

const columnHelper = createColumnHelper<SiarExportProject>()

const VIRTUOSO_HEIGHT = 380

/* TableBodyRow / TableHeader horizontal padding: 2 × 24px */
const ROW_HORIZONTAL_PADDING = 48

const cellTextStyle = {width: '100%', minWidth: 0} as const
const cellStyle = {minWidth: 0, flexShrink: 0, overflow: 'hidden'} as const
const headerCellStyle = {flexShrink: 0} as const

export const SiarExportDetailTable = ({
    items,
    isSelected,
    onToggle,
    onToggleAll,
    masterChecked,
    masterIndeterminate
}: SiarExportDetailTableProps) => {
    const {t} = useTranslation()

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'select',
                size: 40,
                header: () => (
                    <Checkbox
                        id="siar-export-detail-master-checkbox"
                        checked={masterChecked}
                        indeterminate={masterIndeterminate}
                        onChange={onToggleAll}
                    />
                ),
                cell: ({row}) => {
                    const isDisabled = row.original.transmissionStatus === 'transmitted'
                    return (
                        <span onClick={e => e.stopPropagation()}>
                            <Checkbox
                                id={`siar-export-detail-row-${row.original.projectId}`}
                                checked={!isDisabled && isSelected(row.original.projectId)}
                                onChange={() => {
                                    if (!isDisabled) onToggle(row.original.projectId)
                                }}
                                disabled={isDisabled}
                            />
                        </span>
                    )
                }
            }),
            columnHelper.accessor('projectCode', {
                id: 'projectCode',
                size: 110,
                header: () => t('projects:bulk_siar_export_column_pri_code'),
                cell: ({row}) => (
                    <Link
                        to={generatePath(routes.PROJECT_DETAILS_PROJECT_DATA.handle.path, {
                            id: row.original.projectId
                        })}
                        onClick={e => e.stopPropagation()}
                    >
                        <TableBodyCellText
                            style={{...cellTextStyle, textDecoration: 'underline', cursor: 'pointer', fontWeight: 500}}
                        >
                            {formatPriCode(row.original.projectCode)}
                        </TableBodyCellText>
                    </Link>
                )
            }),
            columnHelper.accessor('patientFullName', {
                id: 'patientFullName',
                size: 180,
                header: () => t('projects:bulk_siar_export_column_patient'),
                cell: ({getValue}) => <TableBodyCellText style={cellTextStyle}>{getValue()}</TableBodyCellText>
            }),
            columnHelper.accessor('startDate', {
                id: 'startDate',
                size: 110,
                header: () => t('projects:start_date'),
                cell: ({getValue}) => (
                    <TableBodyCellText style={cellTextStyle}>{formatDate(getValue())}</TableBodyCellText>
                )
            }),
            columnHelper.accessor('expirationDate', {
                id: 'expirationDate',
                size: 110,
                header: () => t('projects:bulk_siar_export_column_expiration'),
                cell: ({getValue}) => (
                    <TableBodyCellText style={cellTextStyle}>{formatDate(getValue())}</TableBodyCellText>
                )
            }),
            columnHelper.accessor('endDate', {
                id: 'endDate',
                size: 160,
                header: () => t('projects:bulk_siar_export_column_end_date'),
                cell: ({getValue}) => (
                    <TableBodyCellText style={cellTextStyle}>{formatDate(getValue())}</TableBodyCellText>
                )
            }),
            columnHelper.accessor('transmissionStatus', {
                id: 'transmissionStatus',
                size: 220,
                header: () => t('projects:bulk_siar_export_column_status'),
                cell: ({getValue}) => {
                    const status = getValue()
                    return (
                        <Badge variant={siarExportTransmissionStatusToBadgeVariant[status]} size="sm">
                            {status === 'transmitted' ? <CheckCircleIcon size={12} /> : <Send01Icon size={12} />}
                            {t(siarExportTransmissionStatusToLabel[status])}
                        </Badge>
                    )
                }
            })
        ],
        [t, masterChecked, masterIndeterminate, onToggleAll, isSelected, onToggle]
    )

    const table = useReactTable({
        data: items,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: row => row.projectId
    })

    const rows = table.getRowModel().rows
    const headerGroups = table.getHeaderGroups()
    const tableMinWidth = table.getTotalSize() + ROW_HORIZONTAL_PADDING

    return (
        <StyledBulkSiarExportPreviewTableWrapper>
            <StyledBulkSiarExportPreviewTableInner style={{minWidth: tableMinWidth}}>
                <TableHeader>
                    {headerGroups.map(headerGroup =>
                        headerGroup.headers.map(header => (
                            <TableHeaderCell
                                key={header.id}
                                cellSize={header.getSize()}
                                isPinned={false}
                                style={headerCellStyle}
                            >
                                <TableHeaderCellLabel>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHeaderCellLabel>
                            </TableHeaderCell>
                        ))
                    )}
                </TableHeader>
                <StyledBulkSiarExportPreviewVirtuosoBody>
                    <Virtuoso
                        style={{height: VIRTUOSO_HEIGHT}}
                        data={rows}
                        totalCount={rows.length}
                        overscan={200}
                        itemContent={(_index, row) => {
                            const isDisabled = row.original.transmissionStatus === 'transmitted'
                            return (
                                <TableBodyRow
                                    isSelected={!isDisabled && isSelected(row.original.projectId)}
                                    onClick={() => {
                                        if (!isDisabled) onToggle(row.original.projectId)
                                    }}
                                    style={{
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        opacity: isDisabled ? 0.55 : 1
                                    }}
                                >
                                    <Flexbox fullWidth>
                                        {row.getVisibleCells().map(cell => (
                                            <TableBodyCell
                                                key={cell.id}
                                                cellSize={cell.column.getSize()}
                                                style={cellStyle}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableBodyCell>
                                        ))}
                                    </Flexbox>
                                </TableBodyRow>
                            )
                        }}
                    />
                </StyledBulkSiarExportPreviewVirtuosoBody>
            </StyledBulkSiarExportPreviewTableInner>
        </StyledBulkSiarExportPreviewTableWrapper>
    )
}
